import React, { useState } from 'react';
import './ContactPage.css';
import { db } from './firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { Link } from 'react-router-dom';

const ContactPage = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newsletter, setNewsletter] = useState('');
  const [newsletterMsg, setNewsletterMsg] = useState('');

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await addDoc(collection(db, 'contact-messages'), {
        ...form,
        submittedAt: serverTimestamp(),
        status: 'pending',
      });
      setSubmitted(true);
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setError('There was an error submitting your message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewsletter = async e => {
    e.preventDefault();
    setNewsletterMsg('');
    if (!newsletter) {
      setNewsletterMsg('Please enter your email.');
      return;
    }
    try {
      await addDoc(collection(db, 'newsletter-subscribers'), {
        email: newsletter,
        subscribedAt: serverTimestamp(),
      });
      setNewsletterMsg('Thank you for subscribing!');
      setNewsletter('');
    } catch {
      setNewsletterMsg('There was an error. Please try again.');
    }
  };

  return (
    <main className="contact-main">
      {/* Hero Banner */}
      <section className="contact-hero">
        <div className="contact-hero-overlay">
          <h1>Get in Touch</h1>
          <p>We're Here to Answer Your Questions, Discuss Projects, or Partner With You.</p>
          <div className="contact-hero-cta">
            <Link to="/become-distributor" className="contact-cta-btn">Become a Distributor</Link>
            <a href="#contact-form" className="contact-cta-btn alt">Request a Callback</a>
          </div>
        </div>
      </section>

      {/* Contact Info Split */}
      <section className="contact-info-split">
        <div className="contact-info-block">
          <h2>📍 Head Office – Global</h2>
          <div className="contact-info-details">
            <div>
              <b>Derma Protec Films HQ</b><br />
              8 The Green, Dover, DE 19901, USA
            </div>
            <div className="contact-info-row">
              <span>✉️ <a href="mailto:info@dermaprotecfilms.com">info@dermaprotecfilms.com</a></span>
            </div>
            <div className="contact-info-row">
              <span>📞 <a href="tel:+13024935595">+1 (302) 493-5595</a></span>
            </div>
          </div>
        </div>
        <div className="contact-info-block">
          <h2>🕒 Business Hours</h2>
          <div className="contact-info-details">
            <div>Monday – Friday</div>
            <div>8:00 AM – 5:00 PM</div>
          </div>
          <div className="contact-map-embed">
            <iframe
              title="Derma Protec HQ Map"
              src="https://www.google.com/maps?q=8+The+Green,+Dover,+DE+19901,+USA&output=embed"
              width="100%"
              height="220"
              style={{ border: 0, borderRadius: 8 }}
              allowFullScreen=""
              loading="lazy"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Contact Form + Quick Message */}
      <section className="contact-form-section" id="contact-form">
        <div className="contact-form-left">
          <h2>Contact Form</h2>
          {submitted ? (
            <div className="contact-success">Thanks for contacting us. Our team will get back to you within 24–48 hours.</div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit}>
              {error && <div className="contact-error">{error}</div>}
              <label><span className="label-row">Full Name <span className="required">*</span></span>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="Your Full Name"
                  disabled={loading}
                />
              </label>
              <label><span className="label-row">Email <span className="required">*</span></span>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="example@example.com"
                  disabled={loading}
                />
              </label>
              <label><span className="label-row">Phone Number <span className="required">*</span></span>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  placeholder="+123456789"
                  disabled={loading}
                />
              </label>
              <label><span className="label-row">Subject <span className="required">*</span></span>
                <select
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select Subject</option>
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Dealership">Dealership</option>
                  <option value="Product Inquiry">Product Inquiry</option>
                  <option value="Others">Others</option>
                </select>
              </label>
              <label><span className="label-row">Message <span className="required">*</span></span>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  placeholder="Type your message here"
                  disabled={loading}
                />
              </label>
              <button type="submit" className="contact-submit-btn" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            </form>
          )}
        </div>
        <div className="contact-form-right">
          <div className="quick-message-block">
            <h3>💬 Need immediate help?</h3>
            <p>Drop us a quick message or connect with a local dealer near you.</p>
            <Link to="/find-dealer" className="quick-message-btn">Find a Dealer Near You</Link>
          </div>
        </div>
      </section>

      {/* Social + Newsletter */}
      <section className="contact-social-newsletter">
        <div className="contact-social">
          <h3>Follow Us:</h3>
          <div className="social-icons">
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="social-icon instagram">
              <i className="fab fa-instagram"></i>
            </a>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="social-icon facebook">
              <i className="fab fa-facebook-f"></i>
            </a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="social-icon linkedin">
              <i className="fab fa-linkedin-in"></i>
            </a>
          </div>
        </div>
        <div className="contact-newsletter">
          <h3>📬 Stay Updated:</h3>
          <form className="newsletter-form" onSubmit={handleNewsletter}>
            <input
              type="email"
              placeholder="Your email address"
              value={newsletter}
              onChange={e => setNewsletter(e.target.value)}
              required
              disabled={!!newsletterMsg && newsletterMsg.startsWith('Thank you')}
            />
            <button type="submit" className="newsletter-btn" disabled={!!newsletterMsg && newsletterMsg.startsWith('Thank you')}>Subscribe</button>
          </form>
          {newsletterMsg && <div className="newsletter-msg">{newsletterMsg}</div>}
        </div>
      </section>
    </main>
  );
};

export default ContactPage; 